/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  GetShadowMCPInventoryServerRequest,
  GetShadowMCPInventoryServerSecurity,
} from "../models/operations/getshadowmcpinventoryserver.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildShadowMCPInventoryServerQuery,
  prefetchShadowMCPInventoryServer,
  queryKeyShadowMCPInventoryServer,
  ShadowMCPInventoryServerQueryData,
} from "./shadowMCPInventoryServer.core.js";
export {
  buildShadowMCPInventoryServerQuery,
  prefetchShadowMCPInventoryServer,
  queryKeyShadowMCPInventoryServer,
  type ShadowMCPInventoryServerQueryData,
};

export type ShadowMCPInventoryServerQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * getShadowMCPInventoryServer access
 *
 * @remarks
 * Get one project-scoped Shadow MCP server inventory URL with usage and policy-bypass state. Requires an authenticated session authorized for org:admin on the active organization.
 */
export function useShadowMCPInventoryServer(
  request: GetShadowMCPInventoryServerRequest,
  security?: GetShadowMCPInventoryServerSecurity | undefined,
  options?: QueryHookOptions<
    ShadowMCPInventoryServerQueryData,
    ShadowMCPInventoryServerQueryError
  >,
): UseQueryResult<
  ShadowMCPInventoryServerQueryData,
  ShadowMCPInventoryServerQueryError
> {
  const client = useGramContext();
  return useQuery({
    ...buildShadowMCPInventoryServerQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * getShadowMCPInventoryServer access
 *
 * @remarks
 * Get one project-scoped Shadow MCP server inventory URL with usage and policy-bypass state. Requires an authenticated session authorized for org:admin on the active organization.
 */
export function useShadowMCPInventoryServerSuspense(
  request: GetShadowMCPInventoryServerRequest,
  security?: GetShadowMCPInventoryServerSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    ShadowMCPInventoryServerQueryData,
    ShadowMCPInventoryServerQueryError
  >,
): UseSuspenseQueryResult<
  ShadowMCPInventoryServerQueryData,
  ShadowMCPInventoryServerQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildShadowMCPInventoryServerQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setShadowMCPInventoryServerData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      projectId: string;
      serverSlug: string;
      gramSession?: string | undefined;
    },
  ],
  data: ShadowMCPInventoryServerQueryData,
): ShadowMCPInventoryServerQueryData | undefined {
  const key = queryKeyShadowMCPInventoryServer(...queryKeyBase);

  return client.setQueryData<ShadowMCPInventoryServerQueryData>(key, data);
}

export function invalidateShadowMCPInventoryServer(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      projectId: string;
      serverSlug: string;
      gramSession?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "access",
      "getShadowMCPInventoryServer",
      ...queryKeyBase,
    ],
  });
}

export function invalidateAllShadowMCPInventoryServer(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "access", "getShadowMCPInventoryServer"],
  });
}
